// AdminDashboard.jsx – Übersicht aller Stammbäume
import React, { useState, useEffect } from 'react';
import { ReturnDialog } from './ReturnDialog';
import './Admin.css';

const STATUS_TABS = [
  { key: 'in_bearbeitung', label: 'In Bearbeitung', color: '#B45309' },
  { key: 'fertiggestellt', label: 'Fertiggestellt',  color: '#065F46' },
  { key: 'erstellt',       label: 'Erstellt',         color: '#6B7280' },
];

export default function AdminDashboard({ api, token, onOpenTree, onLogout }) {
  const [trees, setTrees] = useState([]);
  const [activeTab, setActiveTab] = useState('in_bearbeitung');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchTrees = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${api}/api/admin/trees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { onLogout(); return; }
      const d = await res.json();
      if (!res.ok) { setError(d.error || 'Fehler beim Laden'); setLoading(false); return; }
      setTrees(d.trees || []);
    } catch {
      setError('Server nicht erreichbar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTrees(); }, []); // eslint-disable-line

  // ── Status eines Stammbaums ändern ────────────────────────────────────
  const [changing, setChanging] = useState(null); // treeCode, dessen Status gerade geändert wird
  const [returnFor, setReturnFor] = useState(null); // Stammbaum, der zurückgegeben werden soll
  const changeStatus = async (tree, newStatus) => {
    const current = tree.status || 'erstellt';
    if (newStatus === current || current === 'erstellt') return; // nicht bestellte Stammbäume gehören dem Kunden
    if (newStatus === 'erstellt') { setReturnFor(tree); return; } // zuerst Nachricht an den Kunden
    const label = STATUS_TABS.find(t => t.key === newStatus)?.label || newStatus;
    if (!window.confirm(`„${tree.name || tree.code}" auf „${label}" setzen?`)) return;
    await doChangeStatus(tree, newStatus, '');
  };
  const doChangeStatus = async (tree, newStatus, message) => {
    setChanging(tree.code);
    try {
      const res = await fetch(`${api}/api/admin/tree/set-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ treeCode: tree.code, status: newStatus, message }),
      });
      if (res.status === 401) { onLogout(); return; }
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { alert(d.error || 'Status konnte nicht geändert werden.'); return; }
      if (newStatus === 'erstellt' && tree.email && d.mailSent === false) {
        alert('Status geändert, aber die E-Mail an den Kunden konnte nicht gesendet werden.');
      }
      setTrees(ts => ts.map(t => t.code === tree.code ? { ...t, status: newStatus } : t));
      setReturnFor(null);
    } catch {
      alert('Server nicht erreichbar.');
    } finally {
      setChanging(null);
    }
  };

  const filtered = trees.filter(t => (t.status || 'erstellt') === activeTab);

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="admin-layout">
      {returnFor && (
        <ReturnDialog
          tree={returnFor}
          email={returnFor.email}
          busy={changing === returnFor.code}
          onCancel={() => setReturnFor(null)}
          onConfirm={msg => doChangeStatus(returnFor, 'erstellt', msg)}
        />
      )}
      <header className="admin-header">
        <div className="admin-header-brand">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1540dc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
          <span>Stammbaum-Memo Admin</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={fetchTrees}>
            Aktualisieren
          </button>
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onLogout}>
            Abmelden
          </button>
        </div>
      </header>

      <div className="admin-tabs">
        {STATUS_TABS.map(tab => {
          const count = trees.filter(t => (t.status || 'erstellt') === tab.key).length;
          return (
            <button
              key={tab.key}
              className={`admin-tab${activeTab === tab.key ? ' admin-tab--active' : ''}`}
              style={activeTab === tab.key ? { borderBottomColor: tab.color, color: tab.color } : {}}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span className="admin-tab-count">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="admin-content">
        {loading && <div className="admin-loading">Lade Stammbäume…</div>}
        {!loading && error && <div className="admin-error-box">{error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div className="admin-empty">Keine Stammbäume in dieser Kategorie.</div>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div className="admin-tree-list">
            {filtered.map(tree => (
              <div key={tree.code} className="admin-tree-card">
                <div className="admin-tree-info">
                  <div className="admin-tree-name">{tree.name || '(Kein Name)'}</div>
                  <div className="admin-tree-meta">
                    <span>{tree.email || '—'}</span>
                    <span>·</span>
                    <span>{tree.personCount} {tree.personCount === 1 ? 'Person' : 'Personen'}</span>
                    <span>·</span>
                    <span>Geändert: {fmtDate(tree.updatedAt)}</span>
                    <span>·</span>
                    <span className="admin-tree-code">{tree.code}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  <select
                    title={(tree.status || 'erstellt') === 'erstellt'
                      ? 'Nicht bestellte Stammbäume gehören dem Kunden – der Status kann nicht geändert werden.'
                      : 'Status ändern'}
                    value={tree.status || 'erstellt'}
                    disabled={changing === tree.code || (tree.status || 'erstellt') === 'erstellt'}
                    onChange={e => changeStatus(tree, e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d0d5e0', fontSize: 13, background: (tree.status || 'erstellt') === 'erstellt' ? '#f3f4f6' : '#fff', cursor: (tree.status || 'erstellt') === 'erstellt' ? 'not-allowed' : 'pointer' }}
                  >
                    {STATUS_TABS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                  {activeTab !== 'erstellt' ? (
                    <button
                      className="admin-btn admin-btn--primary admin-btn--sm"
                      onClick={() => onOpenTree(tree)}
                    >
                      Öffnen
                    </button>
                  ) : (
                    <span className="admin-no-access">Kein Zugriff</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
